import { useState } from 'react';

import { Archive, Ban, Boxes, CircleCheck, MapPinPlus, Package, PackageCheck, Plus, Truck } from 'lucide-react';

import { DeliveryState, GetDeliveriesQuery } from 'data/__generated__';

import { ActionBar } from 'components/ActionBar';
import { AbortDeliveriesPopup } from './Popups/AbortDeliveriesPopup';
import { ArchiveDeliveriesPopup } from './Popups/ArchiveDeliveriesPopup';
import { CreateBundlesPopup } from './Popups/CreateBundlesPopup';
import { CreateDeliveryPopup } from './Popups/CreateDeliveryPopup';
import { CreateSiteDeliveryPopup } from './Popups/CreateSiteDeliveryPopup';
import { MarkAsDeliveredPopup } from './Popups/MarkAsDeliveredPopup';
import { MarkAsInProgressPopup } from './Popups/MarkAsInProgressPopup';
import { MarkAsPackagingPopup } from './Popups/MarkAsPackagingPopup';
import { MarkAsReadyPopup } from './Popups/MarkAsReadyPopup';

type DeliveryActionBarProps = {
    deliveries: GetDeliveriesQuery['deliveries'];
    selectedDeliveryIds: string[];
};

// Barre d'actions partagée par toutes les vues de livraisons (kanban / timeline / tableau).
// Elle est la source unique des actions « Marquer comme… » + « Autres actions » et porte les popups
// associées. Les vues ne connaissent que la sélection ; les actions vivent ici.
export const DeliveryActionBar = ({ deliveries, selectedDeliveryIds }: DeliveryActionBarProps) => {
    const [isAbortDeliveriesPopupOpen, setIsAbortDeliveriesPopupOpen] = useState<boolean>(false);
    const [isArchiveDeliveriesPopupOpen, setIsArchiveDeliveriesPopupOpen] = useState<boolean>(false);
    const [isCreateDeliveryPopupOpen, setIsCreateDeliveryPopupOpen] = useState<boolean>(false);
    const [isCreateSiteDeliveryPopupOpen, setIsCreateSiteDeliveryPopupOpen] = useState<boolean>(false);
    const [isMarkAsDeliveredPopupOpen, setIsMarkAsDeliveredPopupOpen] = useState<boolean>(false);
    const [isMarkAsInProgressPopupOpen, setIsMarkAsInProgressPopupOpen] = useState<boolean>(false);
    const [isMarkAsPackagingPopupOpen, setIsMarkAsPackagingPopupOpen] = useState<boolean>(false);
    const [isMarkAsReadyPopupOpen, setIsMarkAsReadyPopupOpen] = useState<boolean>(false);
    const [isCreateBundlesPopupOpen, setIsCreateBundlesPopupOpen] = useState<boolean>(false);

    const actionSections = [
        {
            title: 'Marquer comme',
            actions: [
                {
                    icon: <Package />,
                    label: 'en préparation',
                    onClick: () => setIsMarkAsPackagingPopupOpen(true),
                    hidden:
                        !selectedDeliveryIds.length ||
                        deliveries.some(
                            ({ _id, state }) => selectedDeliveryIds.includes(_id) && state !== DeliveryState.Created,
                        ),
                },
                {
                    icon: <PackageCheck />,
                    label: 'prête(s)',
                    onClick: () => setIsMarkAsReadyPopupOpen(true),
                    hidden:
                        !selectedDeliveryIds.length ||
                        deliveries.some(
                            ({ _id, state }) => selectedDeliveryIds.includes(_id) && state !== DeliveryState.Packaging,
                        ),
                },
                {
                    icon: <Truck />,
                    label: 'en livraison',
                    onClick: () => setIsMarkAsInProgressPopupOpen(true),
                    hidden:
                        !selectedDeliveryIds.length ||
                        deliveries.some(
                            ({ _id, state }) => selectedDeliveryIds.includes(_id) && state !== DeliveryState.Ready,
                        ),
                },
                {
                    icon: <CircleCheck />,
                    label: 'livrée(s)',
                    onClick: () => setIsMarkAsDeliveredPopupOpen(true),
                    hidden:
                        !selectedDeliveryIds.length ||
                        deliveries.some(
                            ({ _id, state }) =>
                                selectedDeliveryIds.includes(_id) &&
                                [
                                    DeliveryState.Aborted,
                                    DeliveryState.Archived,
                                    DeliveryState.Created,
                                    DeliveryState.Delivered,
                                    DeliveryState.Packaging,
                                ].includes(state),
                        ),
                },
                {
                    icon: <Ban />,
                    label: 'annulée(s)',
                    onClick: () => setIsAbortDeliveriesPopupOpen(true),
                    hidden:
                        !selectedDeliveryIds.length ||
                        deliveries.some(
                            ({ _id, state }) =>
                                selectedDeliveryIds.includes(_id) &&
                                [
                                    DeliveryState.Aborted,
                                    DeliveryState.Archived,
                                    DeliveryState.Delivered,
                                    DeliveryState.InProgress,
                                ].includes(state),
                        ),
                },
                {
                    icon: <Archive />,
                    label: 'archivée(s)',
                    onClick: () => setIsArchiveDeliveriesPopupOpen(true),
                    hidden:
                        !selectedDeliveryIds.length ||
                        deliveries.some(
                            ({ _id, state }) =>
                                selectedDeliveryIds.includes(_id) &&
                                [
                                    DeliveryState.Archived,
                                    DeliveryState.Created,
                                    DeliveryState.InProgress,
                                    DeliveryState.NotDelivered,
                                    DeliveryState.Packaging,
                                    DeliveryState.Ready,
                                ].includes(state),
                        ),
                },
            ],
        },
        {
            title: 'Autres actions',
            actions: [
                {
                    icon: <Plus />,
                    label: 'Créer une livraison freefood',
                    onClick: () => setIsCreateDeliveryPopupOpen(true),
                },
                {
                    icon: <MapPinPlus />,
                    label: 'Créer une livraison store',
                    onClick: () => setIsCreateSiteDeliveryPopupOpen(true),
                },
                {
                    icon: <Boxes />,
                    label: 'Créer des bundles',
                    onClick: () => setIsCreateBundlesPopupOpen(true),
                },
            ],
        },
    ];

    return (
        <>
            <ActionBar actionSections={actionSections} isInitiallyOpen={!!selectedDeliveryIds.length} />
            <AbortDeliveriesPopup
                deliveries={deliveries}
                selectedDeliveryIds={selectedDeliveryIds}
                isOpen={isAbortDeliveriesPopupOpen}
                setIsOpen={setIsAbortDeliveriesPopupOpen}
            />
            <ArchiveDeliveriesPopup
                deliveries={deliveries}
                selectedDeliveryIds={selectedDeliveryIds}
                isOpen={isArchiveDeliveriesPopupOpen}
                setIsOpen={setIsArchiveDeliveriesPopupOpen}
            />
            <CreateDeliveryPopup isOpen={isCreateDeliveryPopupOpen} setIsOpen={setIsCreateDeliveryPopupOpen} />
            <CreateSiteDeliveryPopup
                isOpen={isCreateSiteDeliveryPopupOpen}
                setIsOpen={setIsCreateSiteDeliveryPopupOpen}
            />
            <MarkAsDeliveredPopup
                deliveries={deliveries}
                selectedDeliveryIds={selectedDeliveryIds}
                isOpen={isMarkAsDeliveredPopupOpen}
                setIsOpen={setIsMarkAsDeliveredPopupOpen}
            />
            <MarkAsInProgressPopup
                deliveries={deliveries}
                selectedDeliveryIds={selectedDeliveryIds}
                isOpen={isMarkAsInProgressPopupOpen}
                setIsOpen={setIsMarkAsInProgressPopupOpen}
            />
            <MarkAsPackagingPopup
                deliveries={deliveries}
                selectedDeliveryIds={selectedDeliveryIds}
                isOpen={isMarkAsPackagingPopupOpen}
                setIsOpen={setIsMarkAsPackagingPopupOpen}
            />
            <MarkAsReadyPopup
                deliveries={deliveries}
                selectedDeliveryIds={selectedDeliveryIds}
                isOpen={isMarkAsReadyPopupOpen}
                setIsOpen={setIsMarkAsReadyPopupOpen}
            />
            <CreateBundlesPopup isOpen={isCreateBundlesPopupOpen} setIsOpen={setIsCreateBundlesPopupOpen} />
        </>
    );
};
